import { Divider } from 'component-library';
import { useTranslation } from 'react-i18next';

import {
  BenchmarkType,
  EcosystemType,
  IndicatorName,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
} from '@/api/rest/resources/types/fact';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { EiiExplainer } from '@/components/NCExplainers/EiiExplainer';
import { COMPLIANCE_LOGOS_EII } from '@/config/constants';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { isNullish } from '@/utils/booleans';

import { EiiGraphTile } from './GraphTile';
import { EiiStatsTile } from './StatsTile';

export const EcosystemIntegrityIndex = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper
      name={t('shared.ncaDetail.details.ecosystemIntegrityIndex.labels.terrestrial.ecosystemIntegrityIndex')}
    >
      <EcosystemIntegrityIndexCard />
    </PlotCardWrapper>
  );
};

const EcosystemIntegrityIndexCard = () => {
  const { t } = useTranslation();

  const factNames = {
    latest: IndicatorName.ecosystem_integrity_index,
    benchmark: IndicatorName.ecosystem_integrity_index_benchmark,
  };

  const facts = usePlotFacts<number>(Object.values(factNames));
  const latestFact = getFactFromFactArray<number>(facts, factNames.latest);

  const eiiType =
    (latestFact?.labels?.find((l) => l.name === PlotReportFactLabelNameEnum.ecosystem_type)?.value as EcosystemType) ??
    EcosystemType.terrestrial; // Default to terrestrial EII

  const highBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
  });
  const moderateBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
  });
  const lowBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
  });
  const veryLowBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.very_low,
  });

  const benchmarks = [
    lowBenchmarkFact?.value,
    veryLowBenchmarkFact?.value,
    moderateBenchmarkFact?.value,
    highBenchmarkFact?.value,
  ];

  const hasData = !latestFact || isNullish(latestFact.value) || typeof latestFact.value !== 'number';

  if (hasData) {
    return null;
  }

  return (
    <NCCard data-testid='ecosystem-integrity-index-nca' explainer={<EiiExplainer eiiType={eiiType} />}>
      <NCCardHeader
        title={t(`shared.ncaDetail.details.ecosystemIntegrityIndex.labels.${eiiType}.ecosystemIntegrityIndex`)}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <EiiGraphTile latestFact={latestFact as PlotReportFactElement<number>} eiiType={eiiType} />
        <EiiStatsTile
          latestFact={latestFact as PlotReportFactElement<number>}
          benchmarks={benchmarks}
          eiiType={eiiType}
        />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_EII} />
    </NCCard>
  );
};
